import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { SubscriptionService } from '../services/subscription.service';

// Define the possible action types
type ActionType = 'cancel' | 'downgrade-free' | 'downgrade-monthly';

interface SubscriptionChangePageProps {
  // Any props if needed
}

/**
 * Page displayed after a user has changed their subscription
 * Handles different types of changes: cancellation, downgrade to free, or downgrade to monthly
 */
const SubscriptionChangePage: React.FC<SubscriptionChangePageProps> = () => {
  const { action, subscriptionId } = useParams<{ action: string; subscriptionId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    const processSubscriptionChange = async () => {
      if (!subscriptionId) {
        setError('Subscription ID is missing');
        setLoading(false);
        return;
      }

      try {
        let result;

        // Process the action based on the URL parameter
        switch (action as ActionType) {
          case 'cancel':
            result = await SubscriptionService.cancelAtPeriodEnd(subscriptionId);
            if (result.success) {
              setSuccess(true);
              setMessage('Your subscription has been set to cancel at the end of the current billing period.');
            } else {
              setError(result.message || 'Failed to cancel subscription');
            }
            break;

          case 'downgrade-free':
            result = await SubscriptionService.downgradeToFree(subscriptionId);
            if (result.success) {
              setSuccess(true);
              setMessage('Your subscription has been downgraded to the free plan.');
            } else {
              setError(result.message || 'Failed to downgrade to free plan');
            }
            break;

          case 'downgrade-monthly':
            result = await SubscriptionService.downgradeYearlyToMonthly(subscriptionId);
            if (result.success) {
              setSuccess(true);
              setMessage('Your subscription has been changed from yearly to monthly billing.');
            } else {
              setError(result.message || 'Failed to change to monthly billing');
            }
            break;

          default:
            setError('Invalid action specified');
        }
      } catch (err: any) {
        setError(err.message || 'An error occurred while processing your request');
      } finally {
        setLoading(false);
      }
    };

    processSubscriptionChange();
  }, [action, subscriptionId]);

  const handleBackToSettings = () => {
    navigate('/settings/plans');
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900 px-4">
        <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
        <p className="mt-4 text-gray-600 dark:text-gray-300">Processing your request...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900 px-4">
      <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
        <div className="p-8">
          {success ? (
            <>
              <div className="flex items-center justify-center w-16 h-16 mx-auto mb-6 bg-green-100 dark:bg-green-900/30 rounded-full">
                <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-center text-gray-800 dark:text-gray-100 mb-4">Subscription Updated</h2>
              <p className="text-center text-gray-600 dark:text-gray-300 mb-6">{message}</p>
            </>
          ) : (
            <>
              <div className="flex items-center justify-center w-16 h-16 mx-auto mb-6 bg-red-100 dark:bg-red-900/30 rounded-full">
                <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-center text-gray-800 dark:text-gray-100 mb-4">Something Went Wrong</h2>
              <p className="text-center text-gray-600 dark:text-gray-300 mb-6">{error}</p>
            </>
          )}
          
          <button
            onClick={handleBackToSettings}
            className="w-full py-2 px-4 bg-blue-500 hover:bg-blue-600 text-white font-semibold rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 transition-colors"
          >
            Back to Subscription Settings
          </button>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionChangePage;
